import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, Route, Routes, useNavigate } from 'react-router-dom';
import { LogOut, RefreshCw } from 'lucide-react';

import { useAuth } from '@/features/auth/useAuth';
import { ChangePasswordPage } from './ChangePasswordPage';

/**
 * Every path while the signed-in employee must change their password: the
 * change form at `/account/password`, and a redirect to it from anywhere else.
 *
 * A venue owner's first sign-in uses the temporary password the seller handed
 * over, and an owner reset by EclipseGG lands here the same way (EclipseGG
 * platform, 2026-10-19). The server refuses every other staff route with 403
 * `password_change_required` until the change is saved, so the app's frame —
 * the top bar's pulse, the ticket drawer, any page's polling — is not rendered
 * at all: there is nothing behind the form that could work.
 *
 * Hook-free on purpose, so a test can read the redirect off the element.
 */
export function ForcedPasswordChangeRoutes() {
  return (
    <Routes>
      <Route path="/account/password" element={<ForcedPasswordChangePage />} />
      <Route path="*" element={<Navigate to="/account/password" replace />} />
    </Routes>
  );
}

/**
 * The existing change form in a minimal frame: the venue's name, so the owner
 * knows where they are, and Sign out, so a wrong account is never a trap
 * (logout is public, so it works here).
 */
export function ForcedPasswordChangePage() {
  const { t } = useTranslation();
  const { venue, reloadSession, signOut } = useAuth();
  const navigate = useNavigate();
  /** The change is saved but the app did not open: Try again stands beside the note. */
  const [stuck, setStuck] = useState(false);
  const [opening, setOpening] = useState(false);

  async function openApp(): Promise<void> {
    setOpening(true);
    // The server cleared the flag in the same save. Re-read `/auth/me` so the
    // app opens up, then leave the form for the landing screen — staying put
    // would show the very same form again, empty, inside the app. Only once
    // the answer is on screen and says so: leaving before that would bounce
    // straight back here, to an empty form without its "Password changed"
    // note, so a read that failed (offline, out of time) keeps the note and
    // offers Try again instead.
    const session = await reloadSession();
    if (session && session.mustChangePassword !== true) {
      navigate('/devices', { replace: true });
      return;
    }
    setOpening(false);
    setStuck(true);
  }

  return (
    <div data-forced-password-change className="flex h-full flex-col">
      <header className="flex shrink-0 items-center gap-3 border-b border-subtle px-4 py-2">
        <span className="min-w-0 flex-1 truncate font-semibold text-content">{venue?.name}</span>
        <button
          type="button"
          data-action="sign-out"
          onClick={() => void signOut()}
          className="flex shrink-0 items-center gap-2 rounded-control px-3 py-1.5 text-sm text-content-secondary hover:bg-hover hover:text-content"
        >
          <LogOut className="h-4 w-4" aria-hidden />
          {t('auth.signOut')}
        </button>
      </header>

      {stuck ? (
        <div className="flex shrink-0 justify-center border-b border-subtle px-4 py-2">
          <button
            type="button"
            data-action="open-app"
            onClick={() => void openApp()}
            disabled={opening}
            className="flex items-center gap-2 rounded-full bg-primary px-4 py-1.5 text-sm font-medium text-primary-fg transition-colors hover:bg-primary-hover disabled:opacity-60"
          >
            <RefreshCw className={`h-4 w-4 ${opening ? 'animate-spin' : ''}`} aria-hidden />
            {t('venueService.tryAgain')}
          </button>
        </div>
      ) : null}

      <main className="min-h-0 flex-1">
        <ChangePasswordPage onChanged={() => void openApp()} />
      </main>
    </div>
  );
}
